import type { AdvisorFeedback, AdvisorStreet as BackendAdvisorStreet } from '@ppwr/convex/players/types'
import { type CardString, Hand } from 'pokersolver'
import {
  ADVISOR_ACTION_CHANGE_COPY,
  ADVISOR_ACTION_IN_BRIEF,
  ADVISOR_ACTION_NOUNS,
  ADVISOR_CORRECT_FOLD_COPY,
  ADVISOR_NOTABLE_ACTION_FALLBACK,
  ADVISOR_SHORT_STREET,
  ADVISOR_SHORT_STREET_FALLBACK,
  ADVISOR_STREET_DELTA_COPY,
  ADVISOR_STREET_DESCRIPTIONS,
  ADVISOR_STREET_DESCRIPTION_FALLBACK,
  ADVISOR_CORRECT_FOLD_HEADLINES,
  ADVISOR_CORRECTION_COPY,
  ADVISOR_FOLD_WIN_COPY,
  ADVISOR_FOLD_WIN_GENERIC_HEADLINES,
  ADVISOR_FOLD_WIN_HEADLINES,
  ADVISOR_LOSS_CORRECTION_COPY,
  ADVISOR_LOSS_PLAN_CHANGE_COPY,
  ADVISOR_OUTCOME_HEADLINES,
  ADVISOR_OUTCOME_LOSS_HEADLINES,
  ADVISOR_OUTCOME_LOSS_SUPPORTING_COPY,
  ADVISOR_OUTCOME_SUPPORTING_COPY,
  ADVISOR_PLAN_CHANGE_COPY,
  pickAdvisorCopy,
  type AdvisorWinFeedbackCategory,
  type FoldWinKind
} from '@ppwr/convex/players/advisor.app.copy'

// Copy banks (win/loss recaps, correct-fold, fold-win, correction/plan-change)
// live in the shared convex module so ADVISOR_OUTPUT.md can enumerate every
// advisor string. This file owns the app-side composition: which pool applies,
// and the dynamic fragments ({street} phrases, action-change descriptions) that
// fill the templates.

export type AdvisorStreet = BackendAdvisorStreet
export type { AdvisorWinFeedbackCategory, FoldWinKind } from '@ppwr/convex/players/advisor.app.copy'

export type AdvisorHandObservation = {
  feedback: AdvisorFeedback
}

export type AdvisorHandOutcome = 'win' | 'loss'
export type AdvisorHandResolution = 'showdown' | 'folds'
export type AdvisorOutcomeContext = {
  foldedPreflopOnly: boolean
  wouldHaveWon: boolean
}

type AdvisorCompletedHandAction = {
  userId: string
  action: string
  boardState: string
}

export function shouldShowAdvisorOutcomeFeedback({
  context,
  foldWasCorrect,
  outcome
}: {
  context: AdvisorOutcomeContext
  foldWasCorrect: boolean
  outcome: AdvisorHandOutcome
}): boolean {
  if (outcome === 'win') return true
  if (!context.foldedPreflopOnly) return true
  return context.wouldHaveWon && foldWasCorrect
}

export function didPlayerOnlyFoldPreflop(actions: readonly AdvisorCompletedHandAction[], userId: string): boolean {
  const voluntaryActions = actions.filter((action) => action.userId === userId && action.action !== 'blind')
  return (
    voluntaryActions.length === 1 && voluntaryActions[0].action === 'fold' && voluntaryActions[0].boardState === 'deal'
  )
}

type AdvisorCompletedHand = {
  communityCards: readonly string[]
  winners: readonly { cards: readonly string[] }[]
  playerTimelines?: readonly {
    players: readonly { userId: string; cards: readonly string[] }[]
  }[]
}

export function didFoldedPlayerHaveWinningHand(hand: AdvisorCompletedHand, userId: string): boolean {
  if (hand.communityCards.length !== 5 || hand.winners.length === 0) return false

  const heroCards = [...(hand.playerTimelines ?? [])]
    .reverse()
    .flatMap((timeline) => timeline.players)
    .find((player) => player.userId === userId && player.cards.length === 2)?.cards
  if (!heroCards) return false

  try {
    const heroHand = Hand.solve([...hand.communityCards, ...heroCards] as CardString[])
    const winnerHands = hand.winners
      .filter((winner) => winner.cards.length === 2)
      .map((winner) => Hand.solve([...hand.communityCards, ...winner.cards] as CardString[]))
    if (winnerHands.length === 0) return false
    return Hand.winners([heroHand, ...winnerHands]).includes(heroHand)
  } catch {
    return false
  }
}

/**
 * True when the player won the hand itself: the main pot (potIndex 0) at
 * showdown, or the whole pot on a win-by-fold. Side-pot-only winners lost to
 * the main-pot winner, so they count as a loss here even though they picked
 * up chips from a side pot — the advisor recaps the hand outcome, not chip
 * flow.
 */
export function didUserWinHand(
  hand: {
    potDistribution?: {
      potWinners?: readonly {
        potIndex: number
        winners: readonly { userId: string }[]
      }[]
    }
    winners?: readonly { userId: string; handRank?: string }[]
  },
  userId: string
): boolean {
  const mainPotWinners = hand.potDistribution?.potWinners?.find((pot) => pot.potIndex === 0)?.winners
  if (mainPotWinners) return mainPotWinners.some((winner) => winner.userId === userId)
  return hand.winners?.some((winner) => winner.userId === userId) ?? false
}

export type AdvisorOutcomeFeedback = {
  id: string
  handNumber: number
  outcome: AdvisorHandOutcome
  resolution: AdvisorHandResolution
  category: AdvisorWinFeedbackCategory
  headline: string
  feedback: AdvisorFeedback
}

export type AdvisorWinFeedback = AdvisorOutcomeFeedback

type AdvisorTransitionContext = NonNullable<NonNullable<AdvisorFeedback['conversation']>['transition']>

function buildCorrectionFragmentArgs(transition: AdvisorTransitionContext): Record<string, string> {
  const actionPhrase = describeActionChange(
    transition.priorAction,
    transition.currentAction,
    transition.priorRecommendedAction,
    transition.priorDeviation
  )
  const streetDelta = describeStreetDelta(transition.priorStreet, transition.currentStreet, transition.changedOn)
  return {
    actionPhrase,
    streetDelta,
    capStreet: capitalize(streetDelta),
    newPlan: describeActionInBrief(transition.currentAction)
  }
}

function correctionSeed(transition: AdvisorTransitionContext): string {
  return `correction:${transition.priorStreet}:${transition.currentStreet}:${transition.priorConcept}:${transition.currentConcept}`
}

function buildLossCorrectionCopy(observations: readonly AdvisorHandObservation[]): string {
  const corrected = [...observations].reverse().find((o) => o.feedback.conversation?.kind === 'corrected')
  const transition = corrected?.feedback.conversation?.transition
  if (!transition) {
    return pickAdvisorCopy('fallback-loss-correction', ADVISOR_LOSS_CORRECTION_COPY.fallback, {})
  }

  return pickAdvisorCopy(`loss-${correctionSeed(transition)}`, ADVISOR_LOSS_CORRECTION_COPY.transition, {
    ...buildCorrectionFragmentArgs(transition)
  })
}

function buildLossPlanChangeCopy(observations: readonly AdvisorHandObservation[]): string {
  const reversed = [...observations].reverse().find((o) => o.feedback.conversation?.kind === 'reversed')
  const transition = reversed?.feedback.conversation?.transition
  if (!transition) {
    return pickAdvisorCopy('fallback-loss-plan-change', ADVISOR_LOSS_PLAN_CHANGE_COPY.fallback, {})
  }

  return pickAdvisorCopy(
    `loss-plan-change:${transition.priorStreet}:${transition.currentStreet}:${transition.priorConcept}:${transition.currentConcept}`,
    ADVISOR_LOSS_PLAN_CHANGE_COPY.transition,
    { ...buildCorrectionFragmentArgs(transition) }
  )
}

// Same action type in both spots: only claim a sizing fix when the prior
// mistake actually was one (tooLarge/tooSmall deviation); otherwise the action
// type is unchanged and the recommendation simply differs — "kept
// betting/checking" is honest.
function describeSameActionChange(currentAction: string, priorWasMistake: boolean, priorDeviation?: string): string {
  if (priorWasMistake && (priorDeviation === 'tooLarge' || priorDeviation === 'tooSmall')) {
    return pickAdvisorCopy('action-change:fixed-sizing', ADVISOR_ACTION_CHANGE_COPY.fixedSizing, {})
  }
  return pickAdvisorCopy('action-change:kept', ADVISOR_ACTION_CHANGE_COPY.kept, {
    brief: describeActionInBrief(currentAction)
  })
}

function describeActionChange(
  priorAction: string,
  currentAction: string,
  priorRecommendedAction: string,
  priorDeviation?: string
): string {
  const priorWasMistake = priorAction !== priorRecommendedAction

  // --- Same action type ---
  if (priorAction === currentAction) {
    return describeSameActionChange(currentAction, priorWasMistake, priorDeviation)
  }

  // --- Correcting a mistake ---
  if (priorWasMistake) {
    return pickAdvisorCopy('action-change:mistake-shift', ADVISOR_ACTION_CHANGE_COPY.mistakeShift, {
      prior: describeActionInBrief(priorAction),
      better: describeActionInBrief(priorRecommendedAction),
      current: describeActionInBrief(currentAction)
    })
  }

  // --- Neutral plan shift (both aligned) ---
  if (priorAction === 'fold') {
    return pickAdvisorCopy('action-change:fold-to-pressure', ADVISOR_ACTION_CHANGE_COPY.foldToPressure, {})
  }
  if (priorAction === 'check' && (currentAction === 'bet' || currentAction === 'raise')) {
    return pickAdvisorCopy('action-change:check-to-bet', ADVISOR_ACTION_CHANGE_COPY.checkToBet, {})
  }
  if ((priorAction === 'bet' || priorAction === 'raise') && currentAction === 'fold') {
    return pickAdvisorCopy('action-change:bet-to-fold', ADVISOR_ACTION_CHANGE_COPY.betToWellTimedFold, {})
  }
  if ((priorAction === 'bet' || priorAction === 'raise') && currentAction === 'check') {
    return pickAdvisorCopy('action-change:bet-to-check', ADVISOR_ACTION_CHANGE_COPY.betToCheck, {})
  }
  return pickAdvisorCopy('action-change:neutral-shift', ADVISOR_ACTION_CHANGE_COPY.neutralShift, {
    prior: describeActionInBrief(priorAction),
    current: describeActionInBrief(currentAction)
  })
}

function capitalize(value: string): string {
  return value.replace(/^./, (c) => c.toUpperCase())
}

function describeActionInBrief(action: string): string {
  return ADVISOR_ACTION_IN_BRIEF[action as keyof typeof ADVISOR_ACTION_IN_BRIEF] ?? action
}

function describeStreetDelta(
  priorStreet: AdvisorStreet,
  currentStreet: AdvisorStreet,
  changedOn: AdvisorStreet
): string {
  const streetOrder: AdvisorStreet[] = ['preflop', 'flop', 'turn', 'river']
  const priorIndex = streetOrder.indexOf(priorStreet)
  const currentIndex = streetOrder.indexOf(currentStreet)

  if (priorIndex < 0 || currentIndex < 0) {
    return pickAdvisorCopy('street-delta:on-street', ADVISOR_STREET_DELTA_COPY.onStreet, { street: changedOn })
  }

  if (priorStreet === 'preflop' && currentStreet === 'flop') {
    return pickAdvisorCopy('street-delta:flop', ADVISOR_STREET_DELTA_COPY.flopCameDown, {})
  }
  if (priorStreet === 'flop' && currentStreet === 'turn') {
    return pickAdvisorCopy('street-delta:turn', ADVISOR_STREET_DELTA_COPY.turnAppeared, {})
  }
  if (priorStreet === 'turn' && currentStreet === 'river') {
    return pickAdvisorCopy('street-delta:river', ADVISOR_STREET_DELTA_COPY.riverCompleted, {})
  }
  if (priorIndex === currentIndex - 2) {
    return pickAdvisorCopy('street-delta:two-street-span', ADVISOR_STREET_DELTA_COPY.twoStreetSpan, {
      priorStreet,
      currentStreet
    })
  }

  return pickAdvisorCopy('street-delta:on-changed-street', ADVISOR_STREET_DELTA_COPY.onStreet, { street: changedOn })
}

function describeStreet(street?: AdvisorStreet): string {
  return (
    ADVISOR_STREET_DESCRIPTIONS[street as keyof typeof ADVISOR_STREET_DESCRIPTIONS] ??
    ADVISOR_STREET_DESCRIPTION_FALLBACK
  )
}

function describeShortStreet(street?: AdvisorStreet): string {
  return ADVISOR_SHORT_STREET[street as keyof typeof ADVISOR_SHORT_STREET] ?? ADVISOR_SHORT_STREET_FALLBACK
}

function getCategory(observations: readonly AdvisorHandObservation[]): AdvisorWinFeedbackCategory {
  if (observations.length === 0) return 'unreviewed'

  const score = observations.reduce((total, observation) => {
    if (observation.feedback.grade === 'aligned') return total + 1
    if (observation.feedback.grade === 'close') return total + 0.6
    return total
  }, 0)
  const alignment = score / observations.length

  if (alignment >= 0.85) return 'onPlan'
  if (alignment >= 0.5) return 'mostlyOnPlan'
  return 'differentRoute'
}

function getNotableObservation(observations: readonly AdvisorHandObservation[]): AdvisorHandObservation | undefined {
  const importanceRank = { minor: 0, meaningful: 1, major: 2 } as const
  return [...observations]
    .filter((observation) => observation.feedback.grade !== 'aligned')
    .sort(
      (left, right) =>
        importanceRank[right.feedback.decision.importance] - importanceRank[left.feedback.decision.importance]
    )[0]
}

function getNotableStreet(observations: readonly AdvisorHandObservation[]): AdvisorStreet | undefined {
  return getNotableObservation(observations)?.feedback.decision.street
}

/** The action the player took at the notable (most important non-aligned)
 * spot, as a noun for recap copy ("your raise before the flop"). Falls back
 * to ADVISOR_NOTABLE_ACTION_FALLBACK ("choice") when the notable action is
 * unrecognized or there is no notable spot to name.
 *
 * When the same street saw MULTIPLE graded actions of the notable type
 * (e.g. an advised preflop call, then a second call when a raise came
 * behind), the bare noun is ambiguous: the recap can read as blaming a call
 * the advisor itself recommended moments earlier. In that case a call is
 * named by what it faced ("your call of a raise to 300 chips"), using the
 * facing chips the backend recorded in the observation's stateFactors. */
function describeNotableAction(observations: readonly AdvisorHandObservation[]): string {
  const notable = getNotableObservation(observations)
  const action = notable?.feedback.decision.actualAction
  const noun = action === undefined ? ADVISOR_NOTABLE_ACTION_FALLBACK : ADVISOR_ACTION_NOUNS[action]
  if (!notable || noun === ADVISOR_NOTABLE_ACTION_FALLBACK) {
    return noun
  }

  const street = notable.feedback.decision.street
  const sameTypeOnStreet = observations.filter(
    (observation) =>
      observation.feedback.decision.actualAction === action && observation.feedback.decision.street === street
  )
  if (sameTypeOnStreet.length < 2 || action !== 'call') {
    return noun
  }

  const facing = notable.feedback.decision.stateFactors
    .find((factor) => factor.startsWith('facing:'))
    ?.slice('facing:'.length)
  return facing ? `${noun} of ${facing}` : noun
}

function isAggressiveAction(action: AdvisorFeedback['decision']['actualAction']): boolean {
  return action === 'bet' || action === 'raise' || action === 'allin'
}

function isValueDrivenAggression(feedback: AdvisorFeedback): boolean {
  // Prefer the structured, server-derived classification — it is computed from
  // the driving conditions and the hero's holding at evaluation time, so it
  // cannot drift when hand-label prose is reworded.
  if (feedback.decision.aggressionMotivation) {
    return feedback.decision.aggressionMotivation === 'value'
  }

  // Legacy fallback for feedback that predates the field (and synthetic
  // feedback): mirror the server's condition sets, then fall back to the
  // hand-label prose.
  const valueReasonKeys = new Set([
    'excellentHand',
    'strongHand',
    'strongMadeHand',
    'riverValueBet',
    'twoPair',
    'pair',
    'pairOf',
    'valueOf'
  ])
  if (feedback.decision.reasonKeys.some((key) => valueReasonKeys.has(key))) return true

  // Mirror the server's motivation sets (advisor.ts): only the explicit
  // bluff markers decide outright; spot conditions (preflop initiative,
  // nobody has bet) used to force 'bluff' here too, misclassifying every
  // made-hand barrel (a top-pair c-bet read as a bluff). With no motivation
  // key present, fall through to the hand-label prose below.
  const bluffReasonKeys = new Set(['riverBluff', 'semiBluffRaise'])
  if (feedback.decision.reasonKeys.some((key) => bluffReasonKeys.has(key))) return false

  // Mirror the server's holding-based fallback (getAggressionMotivation in
  // advisor.ts): a made hand makes the aggression value even when a draw
  // rides along ('top pair with a flush draw'), and only a pure draw or no
  // made hand is a bluff. Prose matching is best-effort (this path only
  // serves feedback that predates the structured field); the made-hand
  // words deliberately exclude bare 'flush'/'straight', which draw labels
  // ('a flush draw') also contain.
  const handFactor = feedback.decision.stateFactors.find((factor) => factor.startsWith('hand:')) ?? ''
  if (/weak|high cards|no pair/i.test(handFactor)) return false
  if (
    /draw|four cards of|one card from/i.test(handFactor) &&
    !/pair|trips|three of a kind|full house|four of a kind|straight flush/i.test(handFactor)
  ) {
    return false
  }
  return true
}

function getFoldWinKind(observations: readonly AdvisorHandObservation[]): FoldWinKind {
  const aggressiveFeedback = [...observations]
    .reverse()
    .find((observation) => isAggressiveAction(observation.feedback.decision.actualAction))?.feedback
  if (!aggressiveFeedback) return 'generic'

  const isValueDriven = isValueDrivenAggression(aggressiveFeedback)
  if (aggressiveFeedback.decision.deviation === 'tooLarge') {
    return isValueDriven ? 'valueTooLarge' : 'bluffTooLarge'
  }
  if (aggressiveFeedback.grade === 'aligned') return isValueDriven ? 'soundValue' : 'soundBluff'
  if (!isAggressiveAction(aggressiveFeedback.decision.recommendedAction)) return 'aggressionAdjustment'
  return 'sizingAdjustment'
}

function buildFoldWinCopy(seed: string, observations: readonly AdvisorHandObservation[]) {
  const kind = getFoldWinKind(observations)
  // A fold-win with no hero aggression (the big-blind walk) must not claim the
  // bet or the pressure took the pot down — the generic kind gets headlines
  // that stay true when the player never bet, raised, or called.
  const headlinePool = kind === 'generic' ? ADVISOR_FOLD_WIN_GENERIC_HEADLINES : ADVISOR_FOLD_WIN_HEADLINES
  return {
    headline: pickAdvisorCopy(`fold-headline:${seed}:${kind}`, headlinePool, {}),
    supportingText: pickAdvisorCopy(`fold-supporting:${seed}:${kind}`, ADVISOR_FOLD_WIN_COPY[kind], {})
  }
}

function buildCorrectionCopy(observations: readonly AdvisorHandObservation[]): string {
  const corrected = [...observations].reverse().find((o) => o.feedback.conversation?.kind === 'corrected')
  const transition = corrected?.feedback.conversation?.transition
  if (!transition) {
    return pickAdvisorCopy('fallback-correction', ADVISOR_CORRECTION_COPY.fallback, {})
  }

  return pickAdvisorCopy(correctionSeed(transition), ADVISOR_CORRECTION_COPY.transition, {
    ...buildCorrectionFragmentArgs(transition)
  })
}

function buildPlanChangeCopy(observations: readonly AdvisorHandObservation[]): string {
  const reversed = [...observations].reverse().find((o) => o.feedback.conversation?.kind === 'reversed')
  const transition = reversed?.feedback.conversation?.transition
  if (!transition) {
    return pickAdvisorCopy('fallback-plan-change', ADVISOR_PLAN_CHANGE_COPY.fallback, {})
  }

  return pickAdvisorCopy(
    `plan-change:${transition.priorStreet}:${transition.currentStreet}:${transition.priorConcept}:${transition.currentConcept}`,
    ADVISOR_PLAN_CHANGE_COPY.transition,
    { ...buildCorrectionFragmentArgs(transition) }
  )
}

export function buildAdvisorWinFeedback({
  gameId,
  handNumber,
  observations,
  resolution = 'showdown'
}: {
  gameId: string
  handNumber: number
  observations: readonly AdvisorHandObservation[]
  resolution?: AdvisorHandResolution
}): AdvisorWinFeedback {
  const category = getCategory(observations)
  const seed = `${gameId}:${handNumber}:${category}`
  const hasCorrection = observations.some((observation) => observation.feedback.conversation?.kind === 'corrected')
  const hasPlanChange = observations.some((observation) => observation.feedback.conversation?.kind === 'reversed')
  const standardCopy = {
    headline: pickAdvisorCopy(`headline:${seed}`, ADVISOR_OUTCOME_HEADLINES[category], {}),
    supportingText: hasCorrection
      ? buildCorrectionCopy(observations)
      : hasPlanChange
        ? buildPlanChangeCopy(observations)
        : pickAdvisorCopy(`supporting:${seed}`, ADVISOR_OUTCOME_SUPPORTING_COPY[category], {
            street: describeStreet(getNotableStreet(observations)),
            action: describeNotableAction(observations)
          })
  }
  const { headline, supportingText } = resolution === 'folds' ? buildFoldWinCopy(seed, observations) : standardCopy

  return {
    id: `${gameId}:${handNumber}:win`,
    handNumber,
    outcome: 'win',
    resolution,
    category,
    headline,
    feedback: {
      grade: 'aligned',
      playerActionLabel: 'Won hand',
      recommendedActionLabel: 'Won hand',
      primaryReason: supportingText,
      decision: {
        street: getNotableStreet(observations) ?? observations.at(-1)?.feedback.decision.street ?? 'river',
        concept: 'handStrength',
        deviation: 'none',
        importance: 'minor',
        actualAction: 'check',
        recommendedAction: 'check',
        reasonKeys: ['winningHand'],
        stateFactors: [`reviewedDecisions:${observations.length}`],
        copyVariantId: `winning:${seed}`
      },
      conversation: { kind: 'continued' }
    }
  }
}

function isCorrectFoldDespiteResult(
  context: AdvisorOutcomeContext,
  observations: readonly AdvisorHandObservation[]
): boolean {
  return (
    context.foldedPreflopOnly &&
    context.wouldHaveWon &&
    observations.some(
      (observation) =>
        observation.feedback.grade === 'aligned' &&
        observation.feedback.decision.street === 'preflop' &&
        observation.feedback.decision.actualAction === 'fold'
    )
  )
}

/** A 'corrected' observation is SUBSTANTIVE — worth the correction recap —
 * when the player either fixed a parameter within the same action (bet too
 * big, then the right size) or adapted to a genuinely NEW recommendation
 * (the plan changed under them). A correction that merely returns to an
 * UNCHANGED recommendation (called when told to fold, then folded when told
 * to fold) is compliance, not adaptation: the expert's plan never changed,
 * the loss traces to the original deviation, and the recap must review THAT
 * (the category pools name it) instead of praising a shift. */
function findSubstantiveCorrection(
  observations: readonly AdvisorHandObservation[]
): AdvisorHandObservation | undefined {
  return [...observations].reverse().find((observation) => {
    if (observation.feedback.conversation?.kind !== 'corrected') return false
    const transition = observation.feedback.conversation?.transition
    if (!transition) {
      // Synthetic/legacy corrected observation without transition detail:
      // assume substantive so the correction recap still renders.
      return true
    }
    const parameterFix = transition.priorAction === transition.priorRecommendedAction
    const recommendationChanged = transition.priorRecommendedAction !== transition.currentRecommendedAction
    return parameterFix || recommendationChanged
  })
}

function buildLossSupportingText({
  category,
  correctFoldDespiteResult,
  hasCorrection,
  hasPlanChange,
  notableStreet,
  seed,
  observations
}: {
  category: AdvisorWinFeedbackCategory
  correctFoldDespiteResult: boolean
  hasCorrection: boolean
  hasPlanChange: boolean
  notableStreet?: AdvisorStreet
  seed: string
  observations: readonly AdvisorHandObservation[]
}): string {
  if (correctFoldDespiteResult) return pickAdvisorCopy(`correct-fold:${seed}`, ADVISOR_CORRECT_FOLD_COPY, {})

  return hasCorrection
    ? buildLossCorrectionCopy(observations)
    : hasPlanChange
      ? buildLossPlanChangeCopy(observations)
      : pickAdvisorCopy(`supporting:${seed}`, ADVISOR_OUTCOME_LOSS_SUPPORTING_COPY[category], {
          street: describeShortStreet(notableStreet),
          action: describeNotableAction(observations)
        })
}

function getLossGrade(
  category: AdvisorWinFeedbackCategory,
  correctFoldDespiteResult: boolean
): AdvisorFeedback['grade'] {
  if (correctFoldDespiteResult || category === 'onPlan') return 'aligned'
  return category === 'differentRoute' ? 'offProfile' : 'close'
}

export function buildAdvisorLossFeedback({
  gameId,
  handNumber,
  observations,
  resolution = 'showdown',
  context = { foldedPreflopOnly: false, wouldHaveWon: false }
}: {
  gameId: string
  handNumber: number
  observations: readonly AdvisorHandObservation[]
  resolution?: AdvisorHandResolution
  context?: AdvisorOutcomeContext
}): AdvisorOutcomeFeedback {
  const category = getCategory(observations)
  const seed = `${gameId}:${handNumber}:loss:${category}`
  const notableStreet = getNotableStreet(observations)
  const correctFoldDespiteResult = isCorrectFoldDespiteResult(context, observations)
  // Only SUBSTANTIVE corrections get the correction recap (see
  // findSubstantiveCorrection); compliance-with-unchanged-plan falls to the
  // category pools, which name the original deviation as the review spot.
  const hasCorrection = Boolean(findSubstantiveCorrection(observations))
  const hasPlanChange = observations.some((observation) => observation.feedback.conversation?.kind === 'reversed')
  const supportingText = buildLossSupportingText({
    category,
    correctFoldDespiteResult,
    hasCorrection,
    hasPlanChange,
    notableStreet,
    seed,
    observations
  })
  const grade = getLossGrade(category, correctFoldDespiteResult)

  return {
    id: `${gameId}:${handNumber}:loss`,
    handNumber,
    outcome: 'loss',
    resolution,
    category,
    headline: correctFoldDespiteResult
      ? pickAdvisorCopy(`correct-fold-headline:${seed}`, ADVISOR_CORRECT_FOLD_HEADLINES, {})
      : pickAdvisorCopy(`headline:${seed}`, ADVISOR_OUTCOME_LOSS_HEADLINES[category], {}),
    feedback: {
      grade,
      playerActionLabel: correctFoldDespiteResult ? 'Folded hand' : 'Lost hand',
      recommendedActionLabel: correctFoldDespiteResult ? 'Fold' : 'Review hand',
      primaryReason: supportingText,
      // The display contract shows `differenceReason` whenever grade !== 'aligned'
      // and otherwise falls back to `primaryReason`. Loss recaps carry a single
      // supporting line, so mirror it into `differenceReason` for non-aligned
      // grades instead of letting the UI silently show the `primaryReason` fallback.
      differenceReason: grade === 'aligned' ? undefined : supportingText,
      decision: {
        street: notableStreet ?? observations.at(-1)?.feedback.decision.street ?? 'river',
        concept: 'handStrength',
        deviation: 'none',
        importance: 'minor',
        actualAction: 'check',
        recommendedAction: 'check',
        reasonKeys: ['losingHand'],
        stateFactors: [`reviewedDecisions:${observations.length}`],
        copyVariantId: `losing:${seed}`
      },
      conversation: { kind: 'continued' }
    }
  }
}

export function buildAdvisorOutcomeFeedback({
  outcome,
  ...args
}: {
  gameId: string
  handNumber: number
  observations: readonly AdvisorHandObservation[]
  outcome: AdvisorHandOutcome
  resolution: AdvisorHandResolution
  context: AdvisorOutcomeContext
}): AdvisorOutcomeFeedback {
  return outcome === 'win' ? buildAdvisorWinFeedback(args) : buildAdvisorLossFeedback(args)
}
